import React, { useState } from "react";
import { Box, Modal, TextField, FormControlLabel, Checkbox } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { createEmail } from "@/firebase/emails";
import { selectUserState } from "@/store/slices/user.slice";
import { useAppSelector } from "@/hooks/common";
import { StyledButton } from "@/components/button";
import { StyleUtils } from "@/constants/style.utils";
import { AppRoutes } from "@/constants/app.routes";
import { subscribeToKlaviyo } from "@/utils/klaviyo";

import { ModalBody, ModalContent, ModalTitle } from "./shared";

interface Props {
  open: boolean;
  onClose: () => void;
}

// This modal is shown when the rating is above 3 stars
export const ModalAbove3: React.FC<Props> = ({ open, onClose }) => {
  const user = useAppSelector(selectUserState).user!;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(true);

  const handleSubmitEmail = async () => {
    if (!email || !email.includes("@") || !acceptMarketing) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Save email to Firebase
      await createEmail(user.phone_number, email);
      
      // Trigger Klaviyo subscription if user accepted marketing
      if (acceptMarketing) {
        console.log("Subscribing to Klaviyo...");
        await subscribeToKlaviyo(email);
      }
      
      onClose();
      navigate(AppRoutes.select_difficulty);
    } catch (error) {
      console.log("Error submitting email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
    navigate(AppRoutes.select_difficulty);
  };

  return (
    <div>
      <Modal open={open} onClose={onClose}>
        <ModalContent>
          <Box mb={2}>
            <ModalTitle title={"VIELEN DANK!"} titleColor={"green"} />
          </Box>

          <Box mb={2}>
            <ModalBody
              text={
                "Wenn ihr möchtet, könnt ihr uns eure E-Mail-Adresse hinterlassen, damit wir euch über neue Spiele informieren können."
              }
            />
          </Box>

          <Box mb={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="E-Mail-Adresse (optional)"
              type="email"
              InputLabelProps={{ shrink: true }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                "& .MuiInputBase-input, & .MuiFormLabel-root, & .MuiOutlinedInput-notchedOutline":
                  {
                    fontSize: StyleUtils.fonts.size(3.5),
                  },
              }}
            />
          </Box>

          <Box mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptMarketing}
                  onChange={(e) => setAcceptMarketing(e.target.checked)}
                  sx={{
                    color: "primary.main",
                    "&.Mui-checked": {
                      color: "primary.main",
                    },
                  }}
                />
              }
              label="Marketing Mails erhalten (optional)"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: StyleUtils.fonts.size(3.5),
                },
              }}
            />
          </Box>

          <Box mb={1}>
            <StyledButton
              onClick={handleSubmitEmail}
              disabled={isSubmitting || !email || !email.includes("@") || !acceptMarketing}
              variant="contained"
            >
              {isSubmitting ? "Wird gespeichert..." : "E-Mail speichern"}
            </StyledButton>
          </Box>

          <Box mb={2}>
            <StyledButton
              onClick={handleSkip}
              disabled={isSubmitting}
              variant="outlined"
            >
              Überspringen
            </StyledButton>
          </Box>
        </ModalContent>
      </Modal>
    </div>
  );
};
